import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

const SECTIONS = [
  { kind: 'LEDGER', title: 'Bank, cash, loans and other accounts', path: 'ledgers' },
  { kind: 'VENDOR', title: 'What you owe vendors', path: 'vendors' },
  { kind: 'CUSTOMER', title: "Customers' unpaid bills", path: 'customers' },
  { kind: 'STOCK', title: 'Stock in hand', path: 'stock' }
];

const EMPTY = {
  LEDGER: { ledgerId: '', side: 'DR', amount: '', notes: '' },
  VENDOR: { vendorId: '', side: 'CR', amount: '', reference: '', notes: '' },
  CUSTOMER: { customerId: '', billNumber: '', billDate: '', dueDate: '', amount: '', notes: '' },
  STOCK: { location: '', itemId: '', unitOfMeasurementId: '', quantity: '', unitCost: '', lotNumber: '', expiryDate: '' }
};

/**
 * Opening balances at the cut-over date: what the business brings to iBigDo. Each one is posted against Opening Balance
 * Equity, so the owner's capital is worked out as the balancing figure. For the Org Admin and Finance Manager.
 */
function OpeningBalances({ active, currency = 'INR' }) {
  const { expiryEnabled } = useAuth();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [date, setDate] = useState('');
  const [forms, setForms] = useState(EMPTY);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v) => f.format(Number(v || 0));
  }, [currency]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, opts] = await Promise.all([axios.get('/api/opening-balances'), axios.get('/api/opening-balances/options')]);
      setData(o.data);
      setOptions(opts.data);
      setDate(o.data.openingDate || '');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load the opening balances'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  const run = async (key, request, success) => {
    setBusy(key);
    try {
      await request();
      toast.success(success);
      await load();
      return true;
    } catch (error) {
      toast.error(errorMessage(error, 'That did not work'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const saveDate = () => run('date', () => axios.put('/api/opening-balances/date', { date }), 'Opening balance date saved');

  const setForm = (kind, patch) => setForms((f) => ({ ...f, [kind]: { ...f[kind], ...patch } }));

  const add = async (section) => {
    const f = forms[section.kind];
    let body;
    if (section.kind === 'STOCK') {
      const [locationType, locationId] = (f.location || ':').split(':');
      body = {
        locationType, locationId: locationId ? Number(locationId) : null, itemId: f.itemId ? Number(f.itemId) : null,
        unitOfMeasurementId: f.unitOfMeasurementId ? Number(f.unitOfMeasurementId) : null,
        quantity: f.quantity === '' ? null : Number(f.quantity), unitCost: f.unitCost === '' ? null : Number(f.unitCost),
        lotNumber: f.lotNumber || null,
        expiryDate: expiryEnabled && f.expiryDate ? f.expiryDate : null
      };
    } else {
      body = { ...f, amount: f.amount === '' ? null : Number(f.amount) };
      if (section.kind === 'LEDGER') body.ledgerId = f.ledgerId ? Number(f.ledgerId) : null;
      if (section.kind === 'VENDOR') body.vendorId = f.vendorId ? Number(f.vendorId) : null;
      if (section.kind === 'CUSTOMER') {
        body.customerId = f.customerId ? Number(f.customerId) : null;
        body.billDate = f.billDate || null;
        body.dueDate = f.dueDate || null;
      }
    }
    const ok = await run(section.kind, () => axios.post(`/api/opening-balances/${section.path}`, body), 'Opening balance added');
    if (ok) setForms((all) => ({ ...all, [section.kind]: EMPTY[section.kind] }));
  };

  const remove = (entry) => {
    if (!window.confirm(`Remove this opening balance (${money(entry.amount)})? Its entry is reversed.`)) return;
    run(`remove-${entry.id}`, () => axios.post(`/api/opening-balances/${entry.id}/remove`), 'Opening balance removed');
  };

  const pickItem = (itemId) => {
    const item = (options?.items || []).find((i) => String(i.itemId) === String(itemId));
    setForm('STOCK', { itemId, unitOfMeasurementId: item?.unitOfMeasurementId ? String(item.unitOfMeasurementId) : forms.STOCK.unitOfMeasurementId });
  };

  if (loading && !data) return <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>;
  if (!data || !options) return null;

  const entries = data.entries || [];
  const hasDate = Boolean(data.openingDate);
  const capital = Number(data.ownersCapital || 0);
  const ledgerName = (id) => (options.ledgers.find((l) => l.id === id) || {}).name;

  return (
    <div className="mt-3">
      <p className="text-muted">
        If your business ran before it started on iBigDo, enter here what it had on the day you started: money in the bank and
        cash, what customers owed you, what you owed vendors and others, and the stock you held. Each figure is posted on that
        date, and the owner&apos;s capital is worked out as the balancing figure.
      </p>

      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col xs="auto">
              <Form.Label className="small mb-1">Opening balances as at</Form.Label>
              <DateInput size="sm" value={date} max={todayISO()}
                disabled={entries.length > 0} onChange={(e) => setDate(e.target.value)} />
            </Col>
            <Col xs="auto">
              {entries.length === 0 && (
                <Button size="sm" disabled={!date || date === data.openingDate || busy === 'date'} onClick={saveDate}>
                  {hasDate ? 'Change date' : 'Set date'}
                </Button>
              )}
            </Col>
            <Col className="small text-muted">
              {entries.length > 0
                ? 'The date is fixed while balances are entered; remove them all to change it.'
                : 'Usually the day before your first transaction in iBigDo, such as 31 March for a new financial year.'}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <TallyImport date={hasDate ? data.openingDate : date} locations={options.locations} money={money} onDone={load} />

      {hasDate && (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}><SummaryCard label="Total debits" value={money(data.totalDebit)} /></Col>
            <Col md={4}><SummaryCard label="Total credits" value={money(data.totalCredit)} /></Col>
            <Col md={4}>
              <SummaryCard label={capital >= 0 ? "Owner's capital (balancing figure)" : 'Deficit (balancing figure)'}
                value={money(Math.abs(capital))} hint="Opening Balance Equity, shown under Capital Account in the Balance Sheet" />
            </Col>
          </Row>

          {(data.suggestions || []).length > 0 && (
            <Alert variant="info" className="small py-2">
              These accounts have an opening balance typed on the account that isn&apos;t in your books yet:
              <ul className="mb-0">
                {data.suggestions.map((s) => (
                  <li key={s.ledgerId}>
                    {s.name}: {money(s.amount)}{' '}
                    <Button size="sm" variant="link" className="p-0 align-baseline"
                      onClick={() => setForm('LEDGER', { ledgerId: String(s.ledgerId), side: Number(s.amount) >= 0 ? 'DR' : 'CR', amount: String(Math.abs(Number(s.amount))) })}>
                      Fill it in below
                    </Button>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {SECTIONS.map((section) => {
            const rows = entries.filter((e) => e.kind === section.kind);
            const f = forms[section.kind];
            return (
              <Card key={section.kind} className="mb-3">
                <Card.Header><h6 className="mb-0">{section.title}</h6></Card.Header>
                <Card.Body>
                  <Row className="g-2 align-items-end mb-2">
                    {section.kind === 'LEDGER' && (
                      <>
                        <Col md={5}>
                          <Form.Label className="small mb-1">Account</Form.Label>
                          <Form.Select size="sm" value={f.ledgerId}
                            onChange={(e) => {
                              const l = options.ledgers.find((x) => String(x.id) === e.target.value);
                              setForm('LEDGER', { ledgerId: e.target.value, side: l ? l.naturalSide : f.side });
                            }}>
                            <option value="">Choose...</option>
                            {options.ledgers.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.group})</option>)}
                          </Form.Select>
                        </Col>
                        <SideSelect value={f.side} onChange={(side) => setForm('LEDGER', { side })} />
                        <AmountInput value={f.amount} onChange={(amount) => setForm('LEDGER', { amount })} />
                        <Col md={2}><Form.Label className="small mb-1">Note</Form.Label><Form.Control size="sm" maxLength={255} value={f.notes} onChange={(e) => setForm('LEDGER', { notes: e.target.value })} /></Col>
                      </>
                    )}
                    {section.kind === 'VENDOR' && (
                      <>
                        <Col md={4}>
                          <Form.Label className="small mb-1">Vendor</Form.Label>
                          <Form.Select size="sm" value={f.vendorId} onChange={(e) => setForm('VENDOR', { vendorId: e.target.value })}>
                            <option value="">Choose...</option>
                            {options.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">It is</Form.Label>
                          <Form.Select size="sm" value={f.side} onChange={(e) => setForm('VENDOR', { side: e.target.value })}>
                            <option value="CR">Owed to them</option>
                            <option value="DR">An advance we paid</option>
                          </Form.Select>
                        </Col>
                        <AmountInput value={f.amount} onChange={(amount) => setForm('VENDOR', { amount })} />
                        <Col md={2}><Form.Label className="small mb-1">Their bill(s)</Form.Label><Form.Control size="sm" maxLength={60} value={f.reference} onChange={(e) => setForm('VENDOR', { reference: e.target.value })} /></Col>
                      </>
                    )}
                    {section.kind === 'CUSTOMER' && (
                      <>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Customer</Form.Label>
                          <Form.Select size="sm" value={f.customerId} onChange={(e) => setForm('CUSTOMER', { customerId: e.target.value })}>
                            <option value="">Choose...</option>
                            {options.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={2}><Form.Label className="small mb-1">Bill no.</Form.Label><Form.Control size="sm" maxLength={50} value={f.billNumber} onChange={(e) => setForm('CUSTOMER', { billNumber: e.target.value })} /></Col>
                        <Col md={2}><Form.Label className="small mb-1">Bill date</Form.Label><DateInput size="sm" max={data.openingDate} value={f.billDate} onChange={(e) => setForm('CUSTOMER', { billDate: e.target.value })} /></Col>
                        <Col md={2}><Form.Label className="small mb-1">Due date</Form.Label><DateInput size="sm" value={f.dueDate} onChange={(e) => setForm('CUSTOMER', { dueDate: e.target.value })} /></Col>
                        <AmountInput label="Still unpaid" value={f.amount} onChange={(amount) => setForm('CUSTOMER', { amount })} />
                      </>
                    )}
                    {section.kind === 'STOCK' && (
                      <>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Where</Form.Label>
                          <Form.Select size="sm" value={f.location} onChange={(e) => setForm('STOCK', { location: e.target.value })}>
                            <option value="">Choose...</option>
                            {options.locations.map((l) => <option key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>{l.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Item</Form.Label>
                          <Form.Select size="sm" value={f.itemId} onChange={(e) => pickItem(e.target.value)}>
                            <option value="">Choose...</option>
                            {options.items.map((i) => <option key={i.itemId} value={i.itemId}>{i.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Unit</Form.Label>
                          <Form.Select size="sm" value={f.unitOfMeasurementId} onChange={(e) => setForm('STOCK', { unitOfMeasurementId: e.target.value })}>
                            <option value="">Choose...</option>
                            {options.units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={1}><Form.Label className="small mb-1">Qty</Form.Label><Form.Control size="sm" type="number" min="0" step="any" value={f.quantity} onChange={(e) => setForm('STOCK', { quantity: e.target.value })} /></Col>
                        <Col md={2}><Form.Label className="small mb-1">Cost per unit</Form.Label><Form.Control size="sm" type="number" min="0" step="any" value={f.unitCost} onChange={(e) => setForm('STOCK', { unitCost: e.target.value })} /></Col>
                        <Col md={1}><Form.Label className="small mb-1">Lot</Form.Label><Form.Control size="sm" maxLength={50} value={f.lotNumber} onChange={(e) => setForm('STOCK', { lotNumber: e.target.value })} /></Col>
                        {expiryEnabled && <Col md={2}><Form.Label className="small mb-1">Expires</Form.Label><DateInput size="sm" value={f.expiryDate || ''} onChange={(e) => setForm('STOCK', { expiryDate: e.target.value })} /></Col>}
                      </>
                    )}
                    <Col xs="auto">
                      <Button size="sm" disabled={busy === section.kind} onClick={() => add(section)}>{busy === section.kind ? 'Adding...' : 'Add'}</Button>
                    </Col>
                  </Row>
                  {rows.length > 0 && (
                    <Table size="sm" bordered hover responsive className="mb-0">
                      <tbody>
                        {rows.map((e) => (
                          <tr key={e.id}>
                            <td>
                              {section.kind === 'CUSTOMER' ? (
                                <>
                                  {options.customers.find((c) => c.id === e.customerId)?.name || e.ledgerName}
                                  <span className="text-muted small ms-2">bill {e.billNumber}, {formatDate(e.billDate)}{e.dueDate ? `, due ${formatDate(e.dueDate)}` : ''}</span>
                                  {Number(e.amountReceived) > 0 && <Badge bg="info" className="ms-2">{money(e.amountReceived)} received</Badge>}
                                </>
                              ) : section.kind === 'STOCK' ? (
                                <>{e.reference} <span className="text-muted small ms-2">{e.ledgerName.replace('Inventory Control - ', '')}, {e.notes}</span></>
                              ) : (
                                <>{section.kind === 'VENDOR' ? (options.vendors.find((v) => v.id === e.vendorId)?.name || e.ledgerName) : (ledgerName(e.ledgerId) || e.ledgerName)}
                                  {e.reference && <span className="text-muted small ms-2">{e.reference}</span>}
                                  {e.notes && section.kind === 'LEDGER' && <span className="text-muted small ms-2">{e.notes}</span>}</>
                              )}
                            </td>
                            <td className="text-end text-nowrap" style={{ width: '12rem' }}>
                              {money(e.amount)} {section.kind === 'VENDOR' ? (e.side === 'CR' ? 'owed' : 'advance') : e.side === 'DR' ? 'Dr' : 'Cr'}
                            </td>
                            <td className="text-end" style={{ width: '6rem' }}>
                              <Button size="sm" variant="outline-danger" disabled={busy === `remove-${e.id}`} onClick={() => remove(e)}>Remove</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}

/**
 * Reads opening balances out of a Tally masters export, shows what each line would become, and brings in the ready ones
 * when asked. Nothing is saved by the preview.
 */
function TallyImport({ date, locations, money, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [location, setLocation] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [showSkipped, setShowSkipped] = useState(true);

  const form = () => {
    const body = new FormData();
    body.append('file', file);
    if (date) body.append('openingDate', date);
    const [locationType, locationId] = location ? location.split(':') : [];
    if (locationType) {
      body.append('locationType', locationType);
      body.append('locationId', locationId);
    }
    return body;
  };

  const send = async (step) => {
    if (!file) { toast.warn('Choose the Tally masters file first'); return; }
    if (!date) { toast.warn('Choose the date your opening balances are as at, above'); return; }
    if (step === 'commit' && !window.confirm(`Bring in ${preview.ready} opening balance${preview.ready === 1 ? '' : 's'} as at ${formatDate(date)}? `
      + 'Accounts that are missing are created. Each entry can be removed later.')) return;
    setBusy(step);
    try {
      const { data } = await axios.post(`/api/opening-balances/tally/${step}`, form());
      if (step === 'commit') {
        toast.success(`${data.ready} opening balance${data.ready === 1 ? '' : 's'} brought in from Tally`);
        setPreview(null);
        setFile(null);
        onDone();
      } else {
        setPreview(data);
      }
    } catch (error) {
      toast.error(errorMessage(error, step === 'commit' ? 'Could not bring in the balances' : 'Could not read the file'));
    } finally {
      setBusy(null);
    }
  };

  const rows = (preview?.rows || []).filter((r) => showSkipped || r.status === 'READY');
  const difference = Number(preview?.difference || 0);

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Import from Tally</h6>
        <Button size="sm" variant="link" className="p-0" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</Button>
      </Card.Header>
      {open && (
        <Card.Body>
          <p className="small text-muted mb-2">
            In Tally, open Gateway of Tally, then Export &gt; Masters, choose All Masters and XML, and upload that file here. Balances
            come in by Tally group: bank, cash, loans, capital and other accounts (created if missing), what customers owe bill by
            bill, what you owe vendors, and stock by godown. Customers, vendors and items must already be in iBigDo with the same
            names; import parties first from Org Admin &gt; Data Import with the same file. You see every line before anything is saved.
          </p>
          <Row className="g-2 align-items-end mb-2">
            <Col md={4}>
              <Form.Label className="small mb-1">Tally masters file (XML)</Form.Label>
              <Form.Control size="sm" type="file" accept=".xml,text/xml,application/xml"
                onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }} />
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Stock with no matching godown</Form.Label>
              <Form.Select size="sm" value={location} onChange={(e) => { setLocation(e.target.value); setPreview(null); }}>
                <option value="">Choose...</option>
                {locations.map((l) => <option key={`${l.type}:${l.id}`} value={`${l.type}:${l.id}`}>{l.name}</option>)}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="outline-primary" disabled={!file || busy !== null} onClick={() => send('preview')}>
                {busy === 'preview' ? 'Reading...' : 'Preview'}
              </Button>
            </Col>
          </Row>
          {preview && (
            <>
              <div className="d-flex flex-wrap gap-3 align-items-center small mb-2">
                <span><Badge bg="success">{preview.ready}</Badge> ready</span>
                <span><Badge bg="secondary">{preview.skipped}</Badge> left out</span>
                <span>Debits {money(preview.totalDebit)}</span>
                <span>Credits {money(preview.totalCredit)}</span>
                <span>
                  {difference === 0 ? 'Debits and credits match'
                    : `${money(Math.abs(difference))} ${difference > 0 ? "goes to owner's capital" : 'deficit'} (Opening Balance Equity)`}
                </span>
                <Form.Check type="switch" id="tally-show-skipped" label="Show lines left out" checked={showSkipped}
                  onChange={(e) => setShowSkipped(e.target.checked)} />
              </div>
              <div style={{ maxHeight: '24rem', overflowY: 'auto' }} className="mb-2">
                <Table size="sm" bordered hover className="mb-0 small">
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bs-body-bg)' }}>
                    <tr><th>In Tally</th><th>Group</th><th className="text-end">Amount</th><th>In iBigDo</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className={r.status === 'READY' ? '' : 'text-muted'}>
                        <td>{r.name}</td>
                        <td>{r.group}</td>
                        <td className="text-end text-nowrap">{money(r.amount)} {r.side === 'DR' ? 'Dr' : 'Cr'}</td>
                        <td>
                          {r.status === 'READY' ? r.action : <><Badge bg="secondary" className="me-1">Left out</Badge>{r.reason}</>}
                          {r.status === 'READY' && r.reason && <div className="text-warning-emphasis">{r.reason}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Button size="sm" disabled={!preview.ready || busy !== null} onClick={() => send('commit')}>
                {busy === 'commit' ? 'Bringing in...' : `Bring in ${preview.ready} line${preview.ready === 1 ? '' : 's'}`}
              </Button>
              <span className="small text-muted ms-2">Lines left out can be fixed and the file previewed again, or entered by hand below.</span>
            </>
          )}
        </Card.Body>
      )}
    </Card>
  );
}

function SummaryCard({ label, value, hint }) {
  return (
    <Card className="h-100">
      <Card.Body className="py-2">
        <div className="small text-muted">{label}</div>
        <div className="fs-5 fw-semibold">{value}</div>
        {hint && <div className="small text-muted">{hint}</div>}
      </Card.Body>
    </Card>
  );
}

function SideSelect({ value, onChange }) {
  return (
    <Col md={2}>
      <Form.Label className="small mb-1">Balance</Form.Label>
      <Form.Select size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="DR">Debit (Dr)</option>
        <option value="CR">Credit (Cr)</option>
      </Form.Select>
    </Col>
  );
}

function AmountInput({ value, onChange, label = 'Amount' }) {
  return (
    <Col md={2}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Control size="sm" type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} />
    </Col>
  );
}

export default OpeningBalances;
